import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  ToggleLeft,
  ToggleRight,
  Shield,
  UserCheck,
  ChevronDown,
  Hospital,
  Mail,
  Star,
  Pencil,
  Briefcase,
  Phone,
  Trash2,
  Eye,
  Camera,
  Save,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Avatar } from '@brainsway/ui';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/network/http';
import { useAuthStore } from '@/features/auth';
import { FormModal, ConfirmModal, MODAL_COLORS } from '@/components/modal';
import {
  useActivateUser,
  useChangeRole,
  useUserDetail,
  useUpdateHospitals,
  useUpdateUser,
} from '../hooks';
import { DeleteUserConfirmModal } from './DeleteUserConfirmModal';
import { useActiveRoles, useActiveEmploymentRoles } from '@/features/admin/hooks';
import { HospitalAssignmentField } from './HospitalAssignmentField';
import { localizedName } from '@/utils/localized';
import { triggerHapticFeedback } from '../../admin/utils/adminAnimations';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useUpload } from '@/features/messaging/hooks/useUpload';
import { UserAvatar } from '@/components/ui';
import { validateEnglishName, validateHebrewName, validatePhone } from '@/utils/nameValidation';
import toast from 'react-hot-toast';
import type { UserListItem, UpdateUserData } from '../types';

function zodEnglishName() {
  return z.string().superRefine((val, ctx) => {
    const err = validateEnglishName(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}
function zodHebrewName() {
  return z.string().superRefine((val, ctx) => {
    const err = validateHebrewName(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}
function zodPhone() {
  return z.string().superRefine((val, ctx) => {
    const err = validatePhone(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}

const editProfileSchema = z.object({
  firstName: zodEnglishName(),
  lastName: zodEnglishName(),
  firstNameHe: zodHebrewName(),
  lastNameHe: zodHebrewName(),
  phone: zodPhone(),
  preferredLanguage: z.enum(['he', 'en']),
});

const FALLBACK_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'EMP'];

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

interface UserDetailModalProps {
  user: UserListItem;
  onClose: () => void;
}

export function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [empRoleDropdownOpen, setEmpRoleDropdownOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    fromRole: string;
    toRole: string;
  }>({ open: false, fromRole: '', toRole: '' });
  const [isOpen, setIsOpen] = useState(true);
  const { hasPermission } = usePermissions();
  const requestClose = useCallback(() => setIsOpen(false), []);

  const [editingHospitals, setEditingHospitals] = useState(false);
  const [editHospitalIds, setEditHospitalIds] = useState<string[]>([]);
  const [editPrimaryId, setEditPrimaryId] = useState<string | undefined>();

  const [editing, setEditing] = useState(false);
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string | null>(null);
  const { upload, isUploading } = useUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  const impersonateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await http.post<{ user: any; accessToken: string; permissions: string[] }>(`/users/${userId}/impersonate`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
      startImpersonation(data.user, data.accessToken, data.permissions);
      navigate('/');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || t('common.error');
      toast.error(message);
    },
  });
  const activateUserMutation = useActivateUser();
  const changeRoleMutation = useChangeRole();
  const updateHospitalsMutation = useUpdateHospitals();
  const updateUserMutation = useUpdateUser();
  const { data: activeRoles } = useActiveRoles();
  const { data: employmentRoles } = useActiveEmploymentRoles();
  const roleOptions = activeRoles && activeRoles.length > 0
    ? activeRoles
    : FALLBACK_ROLES.map((code) => ({ code, name: code, nameEn: code, id: code, color: null, icon: null, level: 0 }));

  const { data: userDetail } = useUserDetail(user.id);
  const userHospitals = userDetail?.userHospitals ?? [];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset: resetForm,
    formState: { errors },
  } = useForm<z.infer<typeof editProfileSchema>>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      firstNameHe: '',
      lastNameHe: '',
      phone: user.phone || '',
      preferredLanguage: (user.preferredLanguage as 'he' | 'en') || 'he',
    },
  });

  const selectedLang = watch('preferredLanguage');

  const startEditing = () => {
    triggerHapticFeedback();
    resetForm({
      firstName: userDetail?.firstName || user.firstName || '',
      lastName: userDetail?.lastName || user.lastName || '',
      firstNameHe: userDetail?.firstNameHe || '',
      lastNameHe: userDetail?.lastNameHe || '',
      phone: userDetail?.phone || user.phone || '',
      preferredLanguage: (userDetail?.preferredLanguage || user.preferredLanguage || 'he') as 'he' | 'en',
    });
    setPendingAvatarUrl(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setPendingAvatarUrl(null);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await upload(file, 'profile');
    if (result?.url) {
      setPendingAvatarUrl(result.url);
    }
    e.target.value = '';
  };

  const onSaveProfile = handleSubmit(async (data) => {
    try {
      triggerHapticFeedback();
      const payload: UpdateUserData = {
        firstName: data.firstName,
        lastName: data.lastName,
        firstNameHe: data.firstNameHe,
        lastNameHe: data.lastNameHe,
        phone: data.phone || null,
        preferredLanguage: data.preferredLanguage,
        ...(pendingAvatarUrl ? { avatarUrl: pendingAvatarUrl } : {}),
      };
      await updateUserMutation.mutateAsync({ id: user.id, data: payload });
      toast.success(t('users.adminProfileUpdated'));
      setEditing(false);
      setPendingAvatarUrl(null);
    } catch {
      toast.error(t('users.updateFailed'));
    }
  });

  const startEditHospitals = () => {
    setEditHospitalIds(userHospitals.map((uh) => uh.hospital.id));
    setEditPrimaryId(userHospitals.find((uh) => uh.isPrimary)?.hospital.id);
    setEditingHospitals(true);
  };

  const handleSaveHospitals = async () => {
    try {
      triggerHapticFeedback();
      await updateHospitalsMutation.mutateAsync({
        userId: user.id,
        hospitalIds: editHospitalIds,
        primaryHospitalId: editPrimaryId,
      });
      toast.success(t('users.hospitalsUpdated'));
      setEditingHospitals(false);
    } catch {
      toast.error(t('users.updateFailed'));
    }
  };

  const fullName = (() => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.firstName || user.lastName || user.email;
  })();

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await activateUserMutation.mutateAsync({
        id: user.id,
        active: user.status !== 'ACTIVE',
      });
      toast.success(
        t(user.status === 'ACTIVE' ? 'users.deactivatedSuccess' : 'users.activatedSuccess')
      );
    } catch {
      toast.error(t('users.updateFailed'));
    }
  };

  const handleRoleChange = (newRole: string) => {
    if (newRole === user.role) {
      setRoleDropdownOpen(false);
      return;
    }
    setConfirmDialog({ open: true, fromRole: user.role, toRole: newRole });
    setRoleDropdownOpen(false);
  };

  const confirmRoleChange = async () => {
    try {
      triggerHapticFeedback();
      await changeRoleMutation.mutateAsync({ id: user.id, role: confirmDialog.toRole });
      setConfirmDialog({ open: false, fromRole: '', toRole: '' });
      toast.success(t('users.roleChangedSuccess'));
    } catch {
      toast.error(t('users.roleChangeFailed'));
    }
  };

  const handleEmploymentRoleChange = async (roleId: string | null) => {
    setEmpRoleDropdownOpen(false);
    const currentId = userDetail?.employmentRoleId ?? user.employmentRoleId ?? null;
    if (roleId === currentId) return;
    try {
      triggerHapticFeedback();
      await updateUserMutation.mutateAsync({ id: user.id, data: { employmentRoleId: roleId } });
      toast.success(t('users.employmentRoleChanged'));
    } catch {
      toast.error(t('users.updateFailed'));
    }
  };

  const currentEmpRole = userDetail?.employmentRole ?? user.employmentRole;

  return (
    <>
      <FormModal
        isOpen={isOpen && !isDeleteOpen}
        onClose={requestClose}
        onExitComplete={!isDeleteOpen ? onClose : undefined}
        title={fullName}
        maxWidth="max-w-lg"
      >
        <div className="space-y-2">
          {/* Identity */}
          {editing ? (
            <div className="rounded-xl p-3 space-y-3" style={cardStyle}>
              {/* Avatar with camera overlay */}
              <div className="flex justify-center">
                <div className="relative">
                  <UserAvatar
                    src={pendingAvatarUrl || user.avatarUrl}
                    name={fullName}
                    size="h-20 w-20"
                    className="text-xl ring-2 ring-white/30 shadow-lg"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="absolute -bottom-1 -end-1 flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer"
                  >
                    {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
              </div>

              {/* Name in English */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1" dir="ltr">{t('auth.nameInEnglish')}</p>
                <div className="flex gap-2" dir="ltr">
                  <div className="flex-1">
                    <input
                      {...register('firstName')}
                      dir="ltr"
                      placeholder={t('auth.firstNameEn')}
                      className="w-full rounded-lg px-2.5 py-1.5 text-sm bg-white/10 border outline-none focus:ring-1 focus:ring-blue-400/50 placeholder:opacity-30"
                      style={{ borderColor: MODAL_COLORS.border }}
                    />
                    {errors.firstName && <p className="mt-0.5 text-[10px] text-red-400">{t(errors.firstName.message!)}</p>}
                  </div>
                  <div className="flex-1">
                    <input
                      {...register('lastName')}
                      dir="ltr"
                      placeholder={t('auth.lastNameEn')}
                      className="w-full rounded-lg px-2.5 py-1.5 text-sm bg-white/10 border outline-none focus:ring-1 focus:ring-blue-400/50 placeholder:opacity-30"
                      style={{ borderColor: MODAL_COLORS.border }}
                    />
                    {errors.lastName && <p className="mt-0.5 text-[10px] text-red-400">{t(errors.lastName.message!)}</p>}
                  </div>
                </div>
              </div>

              {/* Name in Hebrew */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1">{t('auth.nameInHebrew')}</p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      {...register('firstNameHe')}
                      dir="rtl"
                      placeholder={t('auth.firstNameHe')}
                      className="w-full rounded-lg px-2.5 py-1.5 text-sm bg-white/10 border outline-none focus:ring-1 focus:ring-blue-400/50 placeholder:opacity-30"
                      style={{ borderColor: MODAL_COLORS.border }}
                    />
                    {errors.firstNameHe && <p className="mt-0.5 text-[10px] text-red-400">{t(errors.firstNameHe.message!)}</p>}
                  </div>
                  <div className="flex-1">
                    <input
                      {...register('lastNameHe')}
                      dir="rtl"
                      placeholder={t('auth.lastNameHe')}
                      className="w-full rounded-lg px-2.5 py-1.5 text-sm bg-white/10 border outline-none focus:ring-1 focus:ring-blue-400/50 placeholder:opacity-30"
                      style={{ borderColor: MODAL_COLORS.border }}
                    />
                    {errors.lastNameHe && <p className="mt-0.5 text-[10px] text-red-400">{t(errors.lastNameHe.message!)}</p>}
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1">{t('users.phone')}</p>
                <input
                  {...register('phone')}
                  dir="ltr"
                  placeholder={t('users.phone')}
                  className="w-full rounded-lg px-2.5 py-1.5 text-sm bg-white/10 border outline-none focus:ring-1 focus:ring-blue-400/50 placeholder:opacity-30"
                  style={{ borderColor: MODAL_COLORS.border }}
                />
                {errors.phone && <p className="mt-0.5 text-[10px] text-red-400">{t(errors.phone.message!)}</p>}
              </div>

              {/* Language */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1">{t('users.language')}</p>
                <div className="flex gap-1.5">
                  {(['he', 'en'] as const).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setValue('preferredLanguage', lang, { shouldDirty: true })}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                        selectedLang === lang
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'bg-white/10 opacity-60 hover:opacity-80'
                      }`}
                      style={selectedLang !== lang ? { border: `1px solid ${MODAL_COLORS.border}` } : undefined}
                    >
                      {lang === 'he' ? 'עברית' : 'English'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save / Cancel */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                  style={actionBtnStyle}
                >
                  <X className="w-3.5 h-3.5" />
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSaveProfile}
                  disabled={updateUserMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white transition-colors cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                >
                  {updateUserMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-3.5 h-3.5" />
                      {t('common.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl p-3" style={cardStyle}>
              <Avatar src={user.avatarUrl} name={fullName} size="lg" className="!flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                  <p className="text-sm truncate opacity-70">{user.email}</p>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Phone className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                    <p className="text-sm opacity-70">{user.phone}</p>
                  </div>
                )}
              </div>
              {hasPermission('action:edit_users') && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="flex items-center gap-1 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                  style={actionBtnStyle}
                >
                  <Pencil className="w-3 h-3" />
                  {t('users.editProfile')}
                </button>
              )}
            </div>
          )}

          {/* Role */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('users.role')}</span>
                <span className="text-sm font-semibold opacity-70">{t(`users.roles.${user.role}`)}</span>
              </div>
              {user.role !== 'SUPER_ADMIN' && hasPermission('action:edit_users') && (
                <div className="relative">
                  <button
                    onClick={() => {
                      triggerHapticFeedback();
                      setRoleDropdownOpen(!roleDropdownOpen);
                      setEmpRoleDropdownOpen(false);
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                    style={actionBtnStyle}
                  >
                    {t('users.changeRole')}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <AnimatePresence>
                    {roleDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute top-full mt-1 right-0 glass-surface z-50 min-w-[140px] rounded-lg overflow-hidden"
                        style={{ ...actionBtnStyle, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      >
                        {roleOptions.map((role) => (
                          <button
                            key={role.code}
                            onClick={() => handleRoleChange(role.code)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/10 transition-colors cursor-pointer ${
                              role.code === user.role ? 'font-bold opacity-50' : ''
                            }`}
                          >
                            {localizedName(role, i18n.language)}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Employment Role */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('employmentRoles.title')}</span>
                <span className="text-sm font-semibold opacity-70">
                  {currentEmpRole ? localizedName(currentEmpRole, i18n.language) : t('users.noEmploymentRole')}
                </span>
              </div>
              <div className="relative">
                <button
                  onClick={() => {
                    triggerHapticFeedback();
                    setEmpRoleDropdownOpen(!empRoleDropdownOpen);
                    setRoleDropdownOpen(false);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                  style={actionBtnStyle}
                >
                  {t('users.changeEmploymentRole')}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <AnimatePresence>
                  {empRoleDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="absolute top-full mt-1 right-0 glass-surface z-50 min-w-[160px] rounded-lg overflow-hidden"
                      style={{ ...actionBtnStyle, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    >
                      <button
                        onClick={() => handleEmploymentRoleChange(null)}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/10 transition-colors cursor-pointer italic ${
                          !currentEmpRole ? 'font-bold opacity-50' : 'opacity-70'
                        }`}
                      >
                        {t('users.noEmploymentRole')}
                      </button>
                      {(employmentRoles || []).map((r) => (
                        <button
                          key={r.id}
                          onClick={() => handleEmploymentRoleChange(r.id)}
                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/10 transition-colors cursor-pointer ${
                            currentEmpRole?.id === r.id ? 'font-bold opacity-50' : ''
                          }`}
                        >
                          {localizedName(r, i18n.language)}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('users.status')}</span>
                <span className="text-sm font-semibold opacity-70">{t(`users.statuses.${user.status}`)}</span>
              </div>
              {user.status !== 'PENDING' && hasPermission('action:edit_users') && (
                <button
                  onClick={handleToggleActive}
                  disabled={activateUserMutation.isPending}
                  className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
                  style={actionBtnStyle}
                >
                  {activateUserMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : user.status === 'ACTIVE' ? (
                    <>
                      <ToggleRight className="w-3.5 h-3.5" />
                      {t('users.deactivate')}
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-3.5 h-3.5" />
                      {t('users.activate')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Hospitals */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Hospital className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('users.hospitals')}</span>
              </div>
              {!editingHospitals ? (
                <button
                  onClick={startEditHospitals}
                  className="flex items-center gap-1 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                  style={actionBtnStyle}
                >
                  <Pencil className="w-3 h-3" />
                  {t('users.manageHospitals')}
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setEditingHospitals(false)}
                    className="px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                    style={actionBtnStyle}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSaveHospitals}
                    disabled={updateHospitalsMutation.isPending}
                    className="px-2.5 py-1 rounded-lg text-xs font-bold text-white transition-colors cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                  >
                    {updateHospitalsMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      t('common.save')
                    )}
                  </button>
                </div>
              )}
            </div>
            {editingHospitals ? (
              <HospitalAssignmentField
                selectedIds={editHospitalIds}
                primaryId={editPrimaryId}
                onChange={(ids, pId) => {
                  setEditHospitalIds(ids);
                  setEditPrimaryId(pId);
                }}
              />
            ) : userHospitals.length === 0 ? (
              <p className="text-xs opacity-50">{t('users.noHospitalsAssigned')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {userHospitals.map((uh) => (
                  <div
                    key={uh.id}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold"
                    style={{ backgroundColor: MODAL_COLORS.surface, border: `1px solid ${MODAL_COLORS.border}` }}
                  >
                    {localizedName(uh.hospital, i18n.language)}
                    {uh.isPrimary && <Star className="w-3 h-3" fill="#f59e0b" stroke="#f59e0b" />}
                    {uh.isAutoAssigned && (
                      <span className="text-[10px] opacity-60 font-normal">
                        {t('hospitalAssignment.autoAssigned')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* View As (Impersonate) */}
          {hasPermission('tab:user_impersonation') && currentUser?.id !== user.id && user.status === 'ACTIVE' && (
            <button
              onClick={() => {
                triggerHapticFeedback();
                impersonateMutation.mutate(user.id);
              }}
              disabled={impersonateMutation.isPending}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
              style={{
                backgroundColor: 'rgba(219, 234, 254, 0.6)',
                border: `1px solid ${MODAL_COLORS.border}`,
                color: '#1d4ed8',
              }}
            >
              {impersonateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              {t('admin.impersonation.viewAs')}
            </button>
          )}

          {/* Delete */}
          {user.role !== 'SUPER_ADMIN' && hasPermission('action:delete_users') && (
            <button
              onClick={() => {
                triggerHapticFeedback();
                setIsDeleteOpen(true);
              }}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
              style={{
                backgroundColor: 'rgba(254, 226, 226, 0.6)',
                border: `1px solid ${MODAL_COLORS.border}`,
                color: '#B91C1C',
              }}
            >
              <Trash2 className="w-4 h-4" />
              {t('users.delete')}
            </button>
          )}
        </div>
      </FormModal>

      {/* Role Change Confirm Dialog */}
      <ConfirmModal
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmRoleChange}
        title={t('users.confirmRoleChange')}
        message={t('users.confirmRoleChangeMessage', {
          userName: fullName,
          fromRole: t(`users.roles.${confirmDialog.fromRole}`),
          toRole: t(`users.roles.${confirmDialog.toRole}`),
        })}
        variant="warning"
        isPending={changeRoleMutation.isPending}
      />

      <DeleteUserConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        user={user}
        onDeleted={requestClose}
      />
    </>
  );
}
